import React from 'react'
import { css } from 'emotion'
import styled from '@emotion/styled'
import Input, { InputProps } from 'antd/es/input'
import { SmallP, P1Bold } from 'src/components/Typography'
import Link from 'src/components/Link'
import { useTheme } from 'src/shared/themeContext'
import { Search } from 'src/icons'
import 'antd/es/input/style/css'

// deprecated constants
import { NEUTRAL_COLORS } from 'src/shared/ColorStyles'
import { straitsXTheme } from 'src/shared/theme'
import { MAIN_FONT, FONT_SIZE } from 'src/shared/TypographyStyles'

const style = `
  -webkit-font-smoothing: antialiased;

  & .ant-input, &.ant-input, &.ant-input-password {
    padding: 0;
    font-family: ${MAIN_FONT};
    font-size: ${FONT_SIZE.P1};
    color: ${NEUTRAL_COLORS.BLACK};
    line-height: 17px;
    padding-left: 15px;
    padding-top: 12px;
    padding-bottom: 13px;

    &::placeholder {
      color: ${NEUTRAL_COLORS.G400};
      font-weight: 400;
    }
  }

  &.ant-input, &.ant-input-password {
    border-radius: 5px;
    border-color: ${NEUTRAL_COLORS.G200};

    &:hover {
      border-color: ${straitsXTheme.brand.primary.default};
    }

    &:focus {
      border-color: ${straitsXTheme.brand.primary.default};
      -webkit-box-shadow: 0 0 0 3px #F0FFF9;
      box-shadow: 0 0 0 3px #F0FFF9;
      color: ${NEUTRAL_COLORS.BLACK}
    }
  }

  & .ant-input, .ant-input-password {
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
    border-color: ${NEUTRAL_COLORS.G200};

    &:hover {
      border-color: ${straitsXTheme.brand.primary.default};
    }

    &:focus {
      border-color: ${straitsXTheme.brand.primary.default};
      -webkit-box-shadow: 0 0 0 3px #F0FFF9;
      box-shadow: 0 0 0 3px #F0FFF9;
      color: ${NEUTRAL_COLORS.BLACK}
    }
  }

  &.ant-input-disabled {
    background-color: ${NEUTRAL_COLORS.G100};
    border-color: ${NEUTRAL_COLORS.G200};

    &:hover {
      border-color: ${NEUTRAL_COLORS.G200};
    }
  }

  & .ant-input-group-addon:not(:last-child) {
    font-family: ${MAIN_FONT};
    color: ${NEUTRAL_COLORS.BLACK};
    background-color: ${NEUTRAL_COLORS.G100};
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
    border-color: ${NEUTRAL_COLORS.G200};
    border-right: 0;
    padding-left: 15px;
    padding-right: 15px;

    + .ant-input-disabled {
      background-color: ${NEUTRAL_COLORS.G100};
      border-color: ${NEUTRAL_COLORS.G200};
      color: ${NEUTRAL_COLORS.G400};

      &:hover {
        border-color: ${NEUTRAL_COLORS.G200};
      }
    }
  }

  & .ant-input-group-addon:last-child {
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
    border-color: ${NEUTRAL_COLORS.G200};
  }

  &.ant-input-search {
    padding: 0;
    font-family: ${MAIN_FONT};
    font-size: ${FONT_SIZE.P1};
    line-height: 17px;
    padding-left: 15px;
    padding-top: 6px;
    padding-bottom: 6px;
    border-radius: 5px;
    border-color: ${NEUTRAL_COLORS.G200};
    width: 132px;

    &::placeholder {
      color: ${NEUTRAL_COLORS.G400};
      font-weight: 400;
    }

    &:hover {
      border-color: ${straitsXTheme.brand.primary.default};
    }

    & > .ant-input-suffix {
      width: 18px;
      margin-right: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &.ant-input-affix-wrapper-focused {
    border-color: ${straitsXTheme.brand.primary.default};
    -webkit-box-shadow: 0 0 0 3px #F0FFF9;
    box-shadow: 0 0 0 3px #F0FFF9;
  }
`
const Asterisk = () => {
  const { error } = useTheme()
  return <span style={{ color: error.text, marginLeft: 2 }}>*</span>
}

const OptionalText = () => {
  return (
    <SmallP style={{ color: NEUTRAL_COLORS.G500, marginLeft: 3 }}>
      Optional
    </SmallP>
  )
}

const Label = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
`

type Props = {
  addonBefore?: string
  placeholder?: string
  disabled?: boolean
  className?: string
  label?: string
  required?: boolean
  optional?: boolean
} & Omit<InputProps, 'addonBefore' | 'placeholder' | 'disabled' | 'className'>

const MainInput = function({
  addonBefore = '',
  placeholder = '',
  disabled = false,
  className = '',
  label,
  required,
  optional = false,
  ...rest
}: Props) {
  return (
    <>
      {label && (
        <Label>
          <P1Bold>
            {label}
            {required && <Asterisk />}
          </P1Bold>
          {optional && <OptionalText />}
        </Label>
      )}
      <Input
        className={`${css(style)} ${className}`}
        addonBefore={addonBefore}
        placeholder={placeholder}
        disabled={disabled}
        {...rest}
      />
    </>
  )
}

MainInput.Password = function({
  addonBefore = '',
  placeholder = '',
  disabled = false,
  className = '',
  label,
  required,
  ...rest
}: Props) {
  return (
    <>
      {label && (
        <Label>
          <P1Bold>
            {label}
            {required && <Asterisk />}
          </P1Bold>
        </Label>
      )}
      <Input.Password
        className={`${css(style)} ${className}`}
        addonBefore={addonBefore}
        placeholder={placeholder}
        disabled={disabled}
        visibilityToggle={false}
        {...rest}
      />
    </>
  )
}

type SearchProps = {
  addonBefore?: string | React.ReactNode
  placeholder?: string
  className?: string
  style?: Record<string, unknown>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: () => void
  onSearch?: () => void
  value?: string
}

MainInput.Search = function({
  addonBefore = '',
  placeholder = '',
  className = '',
  onChange,
  onSearch,
  ...rest
}: SearchProps) {
  const {
    neutral,
    text,
    brand: { primary }
  } = useTheme()
  const searchStyle = `
    & .ant-input-group-addon:not(:last-child) {
      font-family: ${MAIN_FONT};
      color: ${text.default};
      background-color: ${neutral.white};
      border-top-left-radius: 5px;
      border-bottom-left-radius: 5px;
      border: 1px solid ${neutral.border};
      border-right: 0;
      padding: ${typeof addonBefore === 'string' ? '0 15px' : '0'};

      & .ant-select {
        margin: 0;
        
        
        &.ant-select-single:not(.ant-select-customize-input) .ant-select-selector, &.ant-select-multiple:not(.ant-select-customize-input) .ant-select-selector {
          height: 38px;
        }
        
        &.ant-select-single.ant-select-focused:not(.ant-select-customize-input) .ant-select-selector, &.ant-select-multiple.ant-select-focused:not(.ant-select-customize-input) .ant-select-selector {
          border-color: ${primary.default};
          -webkit-box-shadow: 0 0 0 2px ${primary.default}33;
          box-shadow: 0 0 0 2px ${primary.default}33;
        }
      } 
    }

    & .ant-input-affix-wrapper, &.ant-input-affix-wrapper {
      border-radius: 5px;
      border: 1px solid ${neutral.border};
      padding: 8px 15px !important;

      &:hover {
        border-color: ${primary.default};
      }
    }

    & .ant-input-affix-wrapper-focused, .ant-input-affix-wrapper:focus {
      border-color: ${primary.default};
      -webkit-box-shadow: 0 0 0 2px ${primary.default}33;
      box-shadow: 0 0 0 2px ${primary.default}33;
    }
  `

  return (
    <Input
      placeholder={placeholder}
      addonBefore={addonBefore}
      onChange={onChange}
      onKeyDown={onSearch}
      className={`${css(searchStyle)}`}
      suffix={
        <Link
          style={{
            display: 'grid',
            justifyContent: 'center'
          }}
          onClick={onSearch}
        >
          <Search style={{ color: neutral.emphasis }} />
        </Link>
      }
      {...rest}
    />
  )
}

type TextAreaProps = {
  placeholder?: string
  className?: string
  style?: Record<string, unknown>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (e: any) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFocus?: any
  label?: string
  required?: boolean
  optional?: boolean
  disabled?: boolean
  value?: string
  rows?: number
  maxLength?: number
}

MainInput.TextArea = function({
  className = '',
  label,
  required = false,
  optional = false,
  ...rest
}: TextAreaProps) {
  const textAreaStyle = `	  
  &.ant-input {	 
    resize: none;	
    border-radius: 5px;	  
    font-family: ${MAIN_FONT};	  
    font-size: ${FONT_SIZE.P1};	  
    color: ${NEUTRAL_COLORS.BLACK};	 
    line-height: 17px;	
    padding-top: 12px;	
    padding-left: 15px;	
    padding-right: 15px;	
    padding-bottom: 12px;	
    border-color: ${NEUTRAL_COLORS.G200};	
  }	
  &.ant-input::placeholder {	
    color: ${NEUTRAL_COLORS.G400};	
    font-weight: 400;	
  }	
  &.ant-input:hover {	
    border-color: ${straitsXTheme.brand.primary.default};	
  }	
  &.ant-input:focus {	
    border-color: ${straitsXTheme.brand.primary.default};	
    -webkit-box-shadow: 0 0 0 3px #F0FFF9;	
    box-shadow: 0 0 0 3px #F0FFF9;	
    color: ${NEUTRAL_COLORS.BLACK}	
  }	
`

  return (
    <>
      {label && (
        <Label>
          <P1Bold>
            {label}
            {required && <Asterisk />}
          </P1Bold>
          {optional && <OptionalText />}
        </Label>
      )}
      <Input.TextArea
        className={`${css(textAreaStyle)} ${className}`}
        {...rest}
      />
    </>
  )
}

MainInput.Group = Input.Group

export default MainInput
